import { useEffect, useRef } from 'react';
import { useHotkeys } from '@mantine/hooks';

type UseViewerHotkeysOptions = {
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
};

/** Document-level keyboard shortcuts for the PDF document preview viewer. */
export function useViewerHotkeys({
  isSearchOpen,
  openSearch,
  closeSearch,
}: UseViewerHotkeysOptions) {
  useHotkeys([
    [
      'mod+F',
      (event) => {
        event.stopPropagation();
        openSearch();
      },
    ],
  ]);

  const isSearchOpenRef = useRef(isSearchOpen);
  isSearchOpenRef.current = isSearchOpen;

  // Escape cannot use Mantine useHotkeys: it only registers bubble-phase listeners
  // on documentElement, with no capture option. While search is open, we need to
  // handle Escape locally and stop propagation so global Escape handlers do not
  // run on the same keydown.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || !isSearchOpenRef.current) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      closeSearch();
    }

    document.documentElement.addEventListener('keydown', handleKeyDown, {
      capture: true,
    });
    return () =>
      document.documentElement.removeEventListener('keydown', handleKeyDown, {
        capture: true,
      });
  }, [closeSearch]);
}
